import React from 'react'
import { Controller, Control } from 'react-hook-form'
import makeStyles from '@mui/styles/makeStyles'
import { IconButton, Chip, Box, Link, TextField } from '@mui/material'

import { Autocomplete } from '@mui/material'
import { orderBy } from 'lodash'

import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { FormSection } from '@modules/requirement/requirement-form/form-section.component'
import { Aspect, Decision, Document } from '@shared/types'
import { aspectsEndpoint } from '@modules/aspects/aspects.page.component'
import { decisionsEndpoint } from '@modules/decisions/decisions.page.component'
import { documentsEndpoint } from '@modules/documents/documents.page.component'
import handleDownload from '@shared/utils/handle-download'
import { originalIcons } from '@shared/components/icons/icons'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'

type WithIdAndName = {
  id: number
  name: string
}

type Props<T extends WithIdAndName> = {
  formControl: Control
  fieldName: 'connectedAspects' | 'connectedDecisions' | 'connectedDocuments'
  label: string
  endpoint: string
  onPressOption?: (object: T) => void
  isPreviewMode?: boolean
  disabled?: boolean
}

export function FormStatusConnected<T extends WithIdAndName>({
  formControl,
  fieldName,
  label,
  endpoint,
  onPressOption,
  isPreviewMode,
  disabled,
}: Props<T>) {
  const classes = useStyles()
  const { data, isLoading } = useQuery<T[]>(endpoint, () => apiJSON(endpoint))

  if (isLoading || !data) {
    return null
  }

  return (
    <Controller
      defaultValue={[]}
      control={formControl}
      name={fieldName}
      render={(formControllerProps) => {
        if (isPreviewMode) {
          if (!formControllerProps.field.value?.length) {
            return <></>
          }

          return (
            <FormSection title={label}>
              {(
                formControllerProps.field.value
                  .map((optionId: number) =>
                    data.find((row) => row.id === optionId)
                  )
                  .filter(Boolean) || []
              ).map((option: T, index: number, array: T[]) => {
                const isLastItem = index === array.length - 1
                if (onPressOption) {
                  return (
                    <>
                      <Link
                        onClick={() => onPressOption(option as T)}
                        style={{ cursor: 'pointer' }}
                      >
                        {option?.name}
                      </Link>
                      {!isLastItem && <span>,&nbsp;</span>}
                    </>
                  )
                }
                return (
                  <div key={option?.id}>
                    {option?.name}
                    {!isLastItem && <span>,&nbsp;</span>}
                  </div>
                )
              })}
            </FormSection>
          )
        }

        return (
          <Autocomplete
            {...formControllerProps}
            value={
              formControllerProps.field.value
                .map((optionId: number) =>
                  data.find((row) => row.id === optionId)
                )
                .filter(Boolean) || []
            }
            multiple
            className={classes.field}
            options={orderBy(data, 'name')}
            getOptionLabel={(option) => option?.name || ''}
            fullWidth
            getOptionKey={(option) => option?.id || ''}
            filterSelectedOptions
            onChange={(event, values) => {
              const selectedIds = values
                .map((aspect: T) => aspect?.id)
                .filter(Boolean)
              formControllerProps.field.onChange(selectedIds)
            }}
            renderInput={(params) => (
              <TextField {...params} label={label} disabled={disabled} />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {onPressOption && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onPressOption(option)
                          }}
                          sx={{ mr: 0.5, p: 0.2, ml: -0.5 }}
                          disabled={disabled}
                        >
                          <originalIcons.Download />
                        </IconButton>
                      )}
                      <span>{option.name}</span>
                    </Box>
                  }
                />
              ))
            }
            noOptionsText={NO_OPTIONS_PLACEHOLDER}
            disabled={disabled}
          />
        )
      }}
    />
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    field: {
      marginTop: theme.spacing(2),
    },
  }
})

export const FormStatusConnectedGroup = ({
  formControl,
  companyID,
  isPreviewMode,
}: {
  formControl: Control
  companyID: number
  isPreviewMode?: boolean
}) => (
  <>
    <FormStatusConnected<Aspect>
      formControl={formControl}
      label="Powiązane aspekty"
      endpoint={`${aspectsEndpoint}/companies/${companyID}`}
      fieldName="connectedAspects"
      isPreviewMode={isPreviewMode}
    />
    <FormStatusConnected<Decision>
      formControl={formControl}
      fieldName="connectedDecisions"
      label="Powiązane decyzje"
      endpoint={`${decisionsEndpoint}/companies/${companyID}`}
      isPreviewMode={isPreviewMode}
      onPressOption={(object) =>
        handleDownload(`decisions/${object.id}/file`, object?.fileName)
      }
    />
    <FormStatusConnected<Document>
      formControl={formControl}
      fieldName="connectedDocuments"
      label="Powiązane dokumenty"
      endpoint={`${documentsEndpoint}/companies/${companyID}`}
      isPreviewMode={isPreviewMode}
      onPressOption={(object) =>
        handleDownload(`documents/${object.id}/file`, object?.fileName)
      }
    />
  </>
)
